"use client";

import PublicLayout from "@/components/layout/PublicLayout";
import { Mail, Phone, MapPin, Send, CheckCircle2, MessageSquare, Clock, ArrowRight, ShieldCheck } from "lucide-react";
import { useState } from "react";
import Link from "next/link";

export default function ContactClient() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      // Success - show confirmation message
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      // Clear success message after 6 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 6000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Contact form error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <PublicLayout>
      <div className="bg-slate-50 min-h-screen font-sans text-slate-900 overflow-x-hidden">

        {/* ── 1. HERO SECTION (MATCHING HOMEPAGE & FEATURES BRANDING) ────────── */}
        <section className="relative overflow-hidden bg-linear-to-br from-slate-950 via-slate-900 to-blue-950 text-white pt-12 pb-16 md:pt-16 md:pb-20">
          {/* Ambient background glow */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center space-y-5">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
              WE&apos;RE HERE FOR YOUR WORKSHOP
            </span>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight text-white tracking-tight">
              Get in Touch with Our{" "}
              <span className="bg-linear-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
                UK Support & Sales Team
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed font-normal">
              Have questions about email booking automation, multi-branch onboarding, or custom setup? Our UK experts are ready to assist.
            </p>

            <p className="text-xs text-slate-400 pt-1">
              ✓ Fast response within 2 hours • ✓ UK-based automotive support team • ✓ Direct WhatsApp line
            </p>
          </div>
        </section>

        {/* ── 2. TRUST STATS BAR ────────────────────────────────────────────── */}
        <section className="py-6 bg-slate-900 border-b border-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-800 text-center">
              <div className="py-2 px-4">
                <p className="text-xl sm:text-2xl font-extrabold text-white">&lt; 2 Hours</p>
                <p className="text-xs text-slate-400 mt-0.5">Average email response time</p>
              </div>
              <div className="py-2 px-4">
                <p className="text-xl sm:text-2xl font-extrabold text-blue-400">UK Team</p>
                <p className="text-xs text-slate-400 mt-0.5">London based automotive experts</p>
              </div>
              <div className="py-2 px-4">
                <p className="text-xl sm:text-2xl font-extrabold text-teal-400">WhatsApp</p>
                <p className="text-xs text-slate-400 mt-0.5">Direct technician & sales chat</p>
              </div>
              <div className="py-2 px-4">
                <p className="text-xl sm:text-2xl font-extrabold text-cyan-400">Free Onboarding</p>
                <p className="text-xs text-slate-400 mt-0.5">1-on-1 walkthrough for garages</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. MAIN CONTACT CONTENT ────────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Contact Channels (5 Cols) */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-blue-600 block mb-1">
                  DIRECT CHANNELS
                </span>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
                  How Can We Help You Today?
                </h2>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">
                  Whether you are looking to automate your workshop intake or need help with your current GarageBoost configuration, our team is standing by.
                </p>
              </div>

              {/* Direct Details Cards */}
              <div className="space-y-4">
                {/* Email Box */}
                <div className="bg-white p-5 rounded-2xl border-2 border-slate-200/90 hover:border-blue-300 transition-all flex items-start gap-4 shadow-xs">
                  <div className="w-11 h-11 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
                    <Mail size={22} />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-slate-900 text-sm">Customer Support & Sales</h3>
                    <a href="mailto:cs@bizzboost.uk" className="text-blue-600 hover:text-blue-700 font-bold text-base block mt-0.5">
                      cs@bizzboost.uk
                    </a>
                    <p className="text-xs text-slate-500 mt-1">Average response time under 2 hours</p>
                  </div>
                </div>

                {/* Phone Box */}
                <div className="bg-white p-5 rounded-2xl border-2 border-slate-200/90 hover:border-teal-300 transition-all flex items-start gap-4 shadow-xs">
                  <div className="w-11 h-11 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center shrink-0">
                    <Phone size={22} />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-slate-900 text-sm">Phone & WhatsApp Support</h3>
                    <a href="tel:07380448187" className="text-teal-700 hover:text-teal-800 font-bold text-base block mt-0.5">
                      07380 448187
                    </a>
                    <p className="text-xs text-slate-500 mt-1">Mon-Fri, 8:00 AM - 6:00 PM GMT • Direct WhatsApp</p>
                  </div>
                </div>

                {/* Location Box */}
                <div className="bg-white p-5 rounded-2xl border-2 border-slate-200/90 flex items-start gap-4 shadow-xs">
                  <div className="w-11 h-11 bg-slate-100 text-slate-700 rounded-xl flex items-center justify-center shrink-0">
                    <MapPin size={22} />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-slate-900 text-sm">London Office Location</h3>
                    <p className="text-slate-700 text-sm mt-0.5 font-medium">
                      Great Portland St, London, W1W 5PF
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Serving MOT stations across England, Scotland & Wales</p>
                  </div>
                </div>
              </div>

              {/* Already a customer card */}
              <div className="bg-linear-to-br from-slate-900 to-blue-950 text-white p-6 rounded-3xl border border-blue-500/20 shadow-md space-y-3">
                <div className="flex items-center gap-2 text-teal-300 font-bold text-xs uppercase tracking-wider">
                  <ShieldCheck size={16} /> Priority Support Portal
                </div>
                <h4 className="text-lg font-bold text-white">Already a GarageBoost Customer?</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Log in directly to your workshop portal to access live in-app chat, technician ticket routing, and instant account assistance.
                </p>
                <Link
                  href="/login"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-400 hover:text-teal-300 pt-1 transition-colors"
                >
                  <span>Go to Customer Dashboard</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Right Column: Contact Message Form (7 Cols) */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 sm:p-10 rounded-3xl border-2 border-slate-200/90 shadow-xl">
                <div className="mb-6">
                  <h3 className="text-2xl font-black text-slate-900">Send Us a Message</h3>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">
                    Fill out the quick form below and our automotive team will get back to you promptly.
                  </p>
                </div>

                {submitted && (
                  <div className="bg-emerald-50 border-2 border-emerald-300 text-emerald-800 p-5 rounded-2xl mb-6 flex items-start gap-3">
                    <CheckCircle2 size={22} className="text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-sm">Thank you for reaching out!</p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        We have received your message and our team will get back to you shortly at the contact details provided.
                      </p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-rose-50 border-2 border-rose-300 text-rose-800 p-4 rounded-2xl mb-6 text-xs sm:text-sm">
                    <strong>Error sending message:</strong> {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="e.g. Sarah Jenkins"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="e.g. sarah@garage.co.uk"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="07123 456789"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                        Subject *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      >
                        <option value="">Select a reason</option>
                        <option value="demo">Request a 1-on-1 Product Demo</option>
                        <option value="pricing">Pricing & Custom Multi-Branch Plan</option>
                        <option value="general">General Platform Inquiry</option>
                        <option value="support">Technical & Email Intake Support</option>
                        <option value="partnership">Partnership & Aggregator Integration</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      placeholder="Tell us about your workshop, how many technicians you have, or how we can assist..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || submitted}
                    className="w-full bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <Send size={18} />
                    <span>{loading ? "Sending Message..." : "Send Message to UK Team"}</span>
                  </button>
                </form>
              </div>
            </div>

          </div>
        </div>

        {/* ── 4. SIGNATURE BOTTOM CTA FLOATING CARD ──────────────────────── */}
        <section className="py-14 sm:py-20 bg-slate-50 relative overflow-hidden">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative rounded-3xl p-8 sm:p-14 md:p-16 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 border border-slate-800 shadow-2xl shadow-blue-950/40 text-white text-center space-y-6">
              
              {/* Ambient inner glows */}
              <div className="absolute top-0 right-1/4 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute inset-0 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

              <div className="relative z-10 space-y-6 max-w-3xl mx-auto">
                <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
                  NO CREDIT CARD REQUIRED
                </span>

                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight">
                  Ready to Automate Your UK Garage?{" "}
                  <span className="bg-linear-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
                    Start 14 Days Free
                  </span>
                </h2>

                <p className="text-sm sm:text-base md:text-lg text-slate-300 leading-relaxed">
                  Join leading MOT stations and automotive workshops across the UK. Get instant access to email intake automation and DVSA lookup.
                </p>

                <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    href="/signup"
                    className="group w-full sm:w-auto bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white px-9 py-4 rounded-xl font-bold text-base sm:text-lg shadow-xl shadow-blue-500/40 hover:shadow-blue-500/60 hover:scale-105 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Start Free 14-Day Trial</span>
                    <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                  </Link>
                  <Link
                    href="/features"
                    className="w-full sm:w-auto bg-white/10 backdrop-blur-md text-white border border-white/20 hover:bg-white/20 px-8 py-4 rounded-xl font-bold text-base sm:text-lg transition-all flex items-center justify-center gap-2"
                  >
                    Explore Features
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </section>

      </div>
    </PublicLayout>
  );
}
